import React, { useState, useRef, useEffect } from 'react';
import { Bars3Icon, BellIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import AppIcon from './AppIcon';
import NotificationCenter from './NotificationCenter';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useBranding } from '../contexts/BrandingContext';

const Header = ({ onMenuClick }) => {
  const { user, changeRole } = useAuth();
  const colors = useTheme();
  const { brandTitle, brandLogo } = useBranding();
  const [showNotificationCenter, setShowNotificationCenter] = useState(false);
  const [showRoleMenu, setShowRoleMenu] = useState(false);
  const roleMenuRef = useRef(null);
  
  // Sample notifications for the bell indicator
  const sampleNotifications = [
    { id: 1, read: false },
    { id: 2, read: false },
    { id: 3, read: true },
  ];

  const unreadCount = sampleNotifications.filter(n => !n.read).length;

  // Close role menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (roleMenuRef.current && !roleMenuRef.current.contains(event.target)) {
        setShowRoleMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleRoleChange = async (newRole) => {
    if (newRole === user?.role) {
      setShowRoleMenu(false);
      return;
    }
    
    const result = await changeRole(newRole);
    if (result.success) {
      setShowRoleMenu(false);
      // Reload the page to refresh all role-based UI
      window.location.reload();
    }
  };

  const roleLabels = {
    coach: 'Coach',
    assistant_coach: 'Assistant Coach',
    player: 'Player',
    parent: 'Parent',
    admin: 'Admin',
    org_admin: 'Organization Admin',
    app_manager: 'App Manager'
  };

  const availableRoles = user?.availableRoles || [];
  const canToggleRoles = user?.canToggleRoles || false;

  return (
    <header
      className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 px-4 shadow-md sm:gap-x-6 sm:px-6 lg:px-8"
      style={{ backgroundColor: colors.primary }}
    >
      {/* Mobile menu button */}
      <button
        type="button"
        className="-m-2.5 p-2.5 text-white lg:hidden"
        onClick={onMenuClick}
      >
        <span className="sr-only">Open sidebar</span>
        <Bars3Icon className="h-6 w-6" aria-hidden="true" />
      </button>

      {/* Separator */}
      <div className="h-6 w-px bg-white/30 lg:hidden" />

      <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
        {/* App Logo/Title */}
        <div className="flex items-center gap-x-2">
          {brandLogo ? (
            <img
              src={brandLogo}
              alt={`${brandTitle} logo`}
              className="h-7 w-auto max-w-[120px] object-contain"
            />
          ) : (
            <AppIcon size={24} circleColor="rgba(255,255,255,0.2)" />
          )}
          <span className="text-lg font-semibold text-white hidden sm:block">{brandTitle}</span>
        </div>

        <div className="flex flex-1"></div>

        {/* Notifications */}
        <div className="flex items-center gap-x-4 lg:gap-x-6">
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowNotificationCenter(true)}
              className="relative p-2 text-white/80 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-transparent"
            >
              <span className="sr-only">View notifications</span>
              <BellIcon className="h-6 w-6" aria-hidden="true" />
              {unreadCount > 0 && (
                <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-red-400 ring-2 ring-white/30" />
              )}
            </button>
          </div>

          {/* User menu */}
          <div className="flex items-center gap-x-4 lg:gap-x-6">
            <div className="flex items-center gap-x-3">
              <div
                className="h-8 w-8 rounded-full flex items-center justify-center border-2 border-white/40"
                style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
              >
                <span className="text-white font-medium text-sm">
                  {user?.name?.charAt(0) || 'U'}
                </span>
              </div>
              <div className="hidden lg:block relative" ref={roleMenuRef}>
                <p className="text-sm font-medium text-white">{user?.name || 'User'}</p>
                {canToggleRoles ? (
                  <button
                    onClick={() => setShowRoleMenu(!showRoleMenu)}
                    className="flex items-center gap-x-1 text-xs text-white/70 hover:text-white capitalize"
                  >
                    {roleLabels[user?.role] || user?.role || 'member'}
                    <ChevronDownIcon className="h-3 w-3" />
                  </button>
                ) : (
                  <p className="text-xs text-white/70 capitalize">{user?.role || 'member'}</p>
                )}

                {/* Role dropdown menu */}
                {showRoleMenu && canToggleRoles && (
                  <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1" role="menu">
                      <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">
                        Switch Role
                      </div>
                      {availableRoles.map((role) => (
                        <button
                          key={role}
                          onClick={() => handleRoleChange(role)}
                          className={`w-full text-left px-4 py-2 text-sm ${
                            user?.role === role
                              ? 'bg-primary-50 text-primary-700 font-medium'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                          role="menuitem"
                        >
                          <div className="flex items-center justify-between">
                            <span>{roleLabels[role] || role}</span>
                            {user?.role === role && (
                              <span className="text-primary-600">✓</span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Notification Center Modal */}
      <NotificationCenter
        isOpen={showNotificationCenter}
        onClose={() => setShowNotificationCenter(false)}
        user={user}
      />
    </header>
  );
};

export default Header;
